import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_DEFAULT } from "../config/xano";
import { Text, Button, LoadingOverlay, TextInput, Group, ActionIcon, Box, Stack, SimpleGrid } from "@mantine/core";
import {
    editorModalPrimaryButtonStyles,
    editorModalPrimaryButtonStylesMd,
    editorModalPrimaryButtonCompactStyles,
    editorModalSecondaryButtonStyles,
    editorModalMdTextInputStyles,
    editorModalGalleryPanelStyle,
    editorModalThumbFrameStyle,
    editorModalFootnoteBoxStyle,
} from "../styleFile";
import { IconRefresh as CachedIcon, IconTrash } from '@tabler/icons-react';
import { useObjectStore } from "../zustand/objects";
import { getFigmaAuthURL, deleteFigmaSync, getUserFigmaFramesAPI, checkForValidFigmaKey, addFigmaFrameFromLink, deleteFigmaFrameFromAccount, updateFigmaFromID } from "../api.js";
import { nanoid } from 'nanoid'

export default function FigmaModalContents({ id }) {
    const primaryTextColor = "var(--mbx-modal-primary-text, #111827)";


    const {
        addObject,
        currentObjectArtboard,
        arrangeUndoRedo
    } = useObjectStore();

    const [hasValidFigmaKey, setHasValidFigmaKey] = useState()
    const [needsRefresh, setNeedsRefresh] = useState(false)
    const [figmaURLs, setFigmaURLs] = useState([])
    const [alreadyImported, setAlreadyImported] = useState(false)
    const [loading, setLoading] = useState(false)

    const getTheFigmaFrames = useCallback(() => {
        setHasValidFigmaKey(true)
        setLoading(false)
        getUserFigmaFramesAPI()
            .then(data => setFigmaURLs([...data]))
            .then(() => { })
            .catch((error) => {
                console.error("Error:", error);
            })
    }, [])

    const checkFigmaKey = useCallback(async () => {
        setLoading(true)
        setNeedsRefresh(false)
        checkForValidFigmaKey()
            .then((e) => (e.user === 200 ? getTheFigmaFrames() : setHasValidFigmaKey(false)))
            .finally(() => setLoading(false))
    }, [getTheFigmaFrames])

    useEffect(() => {
        checkFigmaKey()
    }, [checkFigmaKey]);



    const rando = useMemo(() => [...Array(30)].map(() => Math.random().toString(36)[2]).join(''), [])

    const updateFigmaStateAsync = useCallback(async () => localStorage.setItem('figmaState', rando), [rando])






    const popup = (figmaAuthURLResponse) => window.open(figmaAuthURLResponse, "popup", "popup=true")


    const handleAuthSubmit = () => {
        setNeedsRefresh(true)
        updateFigmaStateAsync().then(() => getFigmaAuthURL(rando)).then((res) => popup(res))
    }


    const postFramesAndRequestFrames = (data) => {
        addFigmaFrameFromLink(data).then((e) => (e === 200) && getTheFigmaFrames())
    }

    const checkChangeInput = (e) => {
        // e.preventDefault()
        const data = e.target.value
        const fileID = data.split('/')
        const frameID = data.split(['node-id='])
        const exists = figmaURLs.some((item) => data && item.frameID === frameID[0].split("&")[0] && item.fileID === fileID[4]);
        e.target.value && exists ? setAlreadyImported(true) : setAlreadyImported(false)
    }

    const handleFigmaFrameLinkSubmit = (e) => {
        e.preventDefault()
        setLoading(true)
        const data = e.target[0].value
        const fileID = data.split('/')
        const frameID = data.split(['node-id='])
        postFramesAndRequestFrames({ frameID: frameID[1].split("&")[0], fileID: fileID[4] })

    }

    const deleteFrameAndRequestAgain = (e) => {
        deleteFigmaFrameFromAccount(e).then(() => getTheFigmaFrames())
    }
    const updateFrameAndRequestAgain = (e) => {
        setLoading(true)
        updateFigmaFromID(e).then(() => getTheFigmaFrames())
    }

    const addFrameObject = (figmaID, figmaURL, figmaSize) => {
        setLoading(true)
        const newID = nanoid()
        console.log('add figma editor page')
        // send update to zustand first (so we have state to grab for undo array)
        addObject({ id: newID, currentObjectArtboard: currentObjectArtboard, undo: false, category: "figma", figmaID: figmaID, figmaURL: figmaURL, figmaSize: figmaSize, object: "figma", imageTexture: null, image: null, bGColour: null, pose: { name: figmaID, modelPath: figmaURL } })
        // send current state to undoActions
        arrangeUndoRedo({
            updateType: "objectAdd",
            artboard: currentObjectArtboard,
            objectID: newID,
            undo: false
        })
        setLoading(false)

    }

    const [deleting, setDeleting] = useState(false)
    const handleDeleteFigmaSyncClick = () => {
        deleteFigmaSync()
            .then(() => setDeleting(true))
            .then(() => checkFigmaKey())
            .then(() => setDeleting(false))
    }


    // can we organise thumbnails by file name? and maybe page?
    // enable refresh xano function 

    return (
        <Box pos="relative" pb={10}>
            <LoadingOverlay loaderProps={{ color: "black" }} visible={deleting} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
            <LoadingOverlay loaderProps={{ color: "black" }} visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
            <Box style={editorModalGalleryPanelStyle}>
                {hasValidFigmaKey && figmaURLs.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py={32} px="md" style={{ lineHeight: 1.5 }}>
                        No frames linked yet. Paste a frame URL below, or link your account first.
                    </Text>
                ) : null}
                {hasValidFigmaKey && figmaURLs.length > 0 ? (
                    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={12}>
                        {figmaURLs.map((figmaData, i) => (
                            <Stack key={i} gap={10} align="center">
                                <Box style={editorModalThumbFrameStyle}>
                                    <img
                                        alt=""
                                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                        src={`${API_DEFAULT}/figmaframes/${figmaData.id}/proxy`}
                                    />
                                </Box>
                                <Group gap={8} justify="center" wrap="nowrap">
                                    <Button
                                        onClick={() =>
                                            addFrameObject(
                                                figmaData.id,
                                                `${API_DEFAULT}/figmaframes/${figmaData.id}/proxy`,
                                                figmaData.size
                                            )
                                        }
                                        styles={editorModalPrimaryButtonCompactStyles}
                                    >
                                        Add
                                    </Button>
                        <ActionIcon
                        variant="light"
                        color="gray"
                        size="md"
                        radius={4}
                        aria-label="Refresh frame"
                        onClick={() => updateFrameAndRequestAgain(figmaData.id)}
                    >
                        <CachedIcon size={18} />
                    </ActionIcon>
                    <ActionIcon
                        variant="light"
                        color="gray"
                        size="md"
                        radius={4}
                        aria-label="Remove frame"
                        onClick={() => deleteFrameAndRequestAgain(figmaData.id)}
                    >
                        <IconTrash size={18} />
                    </ActionIcon>
                                </Group>
                            </Stack>
                        ))}
                    </SimpleGrid>
                ) : null}
                {!hasValidFigmaKey ? (
                    <Text size="sm" c="dimmed" ta="center" py={32} px="md" style={{ lineHeight: 1.5 }}>
                        Link your Figma account to load frames from your files.
                    </Text>
                ) : null}
            </Box>
            <Stack gap="lg" p={20}>
                <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                    <Text size="sm" fw={600} c={primaryTextColor} maw={320} style={{ lineHeight: 1.45 }}>
                        Link your account and paste a frame URL to import.
                    </Text>
                    {needsRefresh ? (
                        <Button radius={4} styles={editorModalPrimaryButtonStyles} onClick={() => checkFigmaKey()}>
                            Check Figma access
                        </Button>
                    ) : hasValidFigmaKey ? (
                        <Button radius={4} styles={editorModalSecondaryButtonStyles} onClick={() => handleDeleteFigmaSyncClick()}>
                            Unlink account
                        </Button>
                    ) : (
                        <Button radius={4} styles={editorModalPrimaryButtonStyles} onClick={() => handleAuthSubmit()}>
                            Link account
                        </Button>
                    )}
                </Group>
                {hasValidFigmaKey && (
                    <form onSubmit={(e) => handleFigmaFrameLinkSubmit(e)} onChange={(e) => checkChangeInput(e)}>
                        <Stack gap={8}>
                            <Text component="label" htmlFor="frameLink" size="sm" fw={600} c={primaryTextColor}>
                                Figma frame link
                            </Text>
                            <Group align="stretch" gap="sm" wrap="nowrap">
                                <TextInput
                                    radius="md"
                                    size="md"
                                    type="text"
                                    id="frameLink"
                                    name="frameLink"
                                    placeholder="https://www.figma.com/design/…"
                                    style={{ flex: 1, minWidth: 0 }}
                                    styles={editorModalMdTextInputStyles}
                                />
                                <Button
                                    radius={4}
                                    type="submit"
                                    disabled={alreadyImported}
                                    styles={editorModalPrimaryButtonStylesMd}
                                >
                                    Submit
                                </Button>
                            </Group>
                            {alreadyImported && (
                                <Text size="sm" c="red" fw={500}>
                                    Frame already linked.
                                </Text>
                            )}
                        </Stack>
                    </form>
                )}
                <Box p="md" style={editorModalFootnoteBoxStyle}>
                    <Text size="xs" c="dimmed" style={{ lineHeight: 1.55 }}>
                        Some networks block embedded Figma previews. If a frame looks blank in the scene, try another network or export as an image.
                    </Text>
                </Box>
            </Stack>
        </Box>
    );
}